/**
 * `tempoCommands` (W1-10a; DESIGN.md §5.2 "Tempo changes", rpe-format F§3.2).
 *
 * The BPM table always has exactly one point at beat 0 (pinned: its beat never changes and it cannot be deleted),
 * beats are unique and every BPM is finite and > 0. Each edit follows the project's edit policy:
 *  - keepBeats (RPE semantics): notes, keys, markers, effects and videos keep their beats and move in time;
 *  - keepTime: they keep their seconds; beats are recomputed exactly as rationals (retime.ts). A beat that RPE
 *    cannot store is rounded to 1/1920 beat, and a value that would then not come after its predecessor (keys of a
 *    track, a hold's end, an effect's end, meter marks) is nudged one 1/1920 step after it. The result counts the
 *    re-timed records and, in `snapped`, every position not at its exact time; the note `cmd.tempo.snapped` also
 *    says how many of those were nudged.
 */

import type { BpmId, BpmPoint } from '../model/types';
import { Beats } from '../time/beat';
import type { Beat } from '../time/types';
import { BeatRetimer, docRatTempo, ratTempo, retimeDocument, solveKeepTime, type OldPoint } from './retime';
import { beatText, bpmOf, isFiniteNumber, type Patch, type Tx } from './structureKit';
import { timingCommands } from './timing';
import type { TempoCommands, TempoEditPolicy } from './types';

function checkBpm(tx: Tx, bpm: number): void {
  if (!(isFiniteNumber(bpm) && bpm > 0)) tx.fail({ key: 'cmd.tempo.bpmInvalid' });
}

function sortedPoints(tx: Tx): OldPoint[] {
  const out: OldPoint[] = [];
  for (const id of tx.idx.bpmSorted()) {
    const p = tx.doc.bpm.get(id);
    if (p !== undefined) out.push(p);
  }
  return out;
}

function pointAt(tx: Tx, t: Beat, except?: BpmId): BpmPoint | undefined {
  for (const p of tx.doc.bpm.values()) if (p.id !== except && Beats.eq(p.t, t)) return p;
  return undefined;
}

/** Checks a non-first point's beat: after 0 and not on another point. */
function checkPointBeat(tx: Tx, t: Beat, except?: BpmId): void {
  if (!Beats.isNormal(t)) tx.fail({ key: 'cmd.badBeat' });
  if (Beats.cmp(t, Beats.ZERO) <= 0) tx.fail({ key: 'cmd.tempo.beforeStart' });
  if (pointAt(tx, t, except) !== undefined) tx.fail({ key: 'cmd.tempo.pointExists', params: { beat: beatText(t) } });
}

function isFirst(tx: Tx, id: BpmId): boolean {
  return tx.idx.bpmSorted()[0] === id;
}

/** The contract result of a BPM edit. */
interface TempoEditResult {
  readonly retimed: number;
  /** Positions not at their exact time (rounded or nudged). */
  readonly snapped: number;
}

const NO_RETIME: TempoEditResult = { retimed: 0, snapped: 0 };

/**
 * Applies a keep-time edit: solves the new BPM list, writes it (`insertNew` creates the added point) and re-times the
 * document. `stableBefore`: records before this beat keep their beats (the tempo before it is unchanged); null when
 * the beat-0 point changes (its tempo also times everything before beat 0).
 */
function keepTimeEdit(
  tx: Tx,
  edited: { readonly id: BpmId | null; readonly t: Beat; readonly bpm: number } | null,
  removed: BpmId | null,
  stableBefore: Beat | null,
): { readonly counts: TempoEditResult; readonly newId: BpmId | null } {
  const old = sortedPoints(tx);
  const oldRt = docRatTempo(tx.doc, tx.idx);
  const solved = solveKeepTime(old, edited, removed);
  if (!solved.ok) {
    tx.fail(solved.reason === 'collide' ? { key: 'cmd.tempo.pointsCollide' } : { key: 'cmd.tempo.outOfRange' });
  }
  let newId: BpmId | null = null;
  if (removed !== null) tx.remove('bpm', removed);
  let retimed = 0;
  for (const p of solved.points) {
    if (p.id === null) {
      newId = tx.newId('bpm');
      tx.insert('bpm', { id: newId, t: p.t, bpm: p.bpm });
      continue;
    }
    const cur = tx.doc.bpm.get(p.id) as BpmPoint;
    const patch: Patch<BpmPoint> = {};
    if (!Beats.eq(cur.t, p.t)) patch.t = p.t;
    if (cur.bpm !== p.bpm) patch.bpm = p.bpm;
    if (Object.keys(patch).length > 0) {
      tx.update('bpm', p.id, patch);
      if (p.id !== edited?.id) retimed++;
    }
  }
  const newRt = ratTempo(solved.points);
  const counts = retimeDocument(tx, new BeatRetimer(oldRt, newRt, stableBefore));
  // `snapped` counts every position that is not at its exact time: rounded BPM points and values, and nudged values.
  const total: TempoEditResult = {
    retimed: counts.retimed + retimed,
    snapped: counts.snapped + counts.nudged + solved.snapped,
  };
  if (total.snapped > 0) tx.note({ key: 'cmd.tempo.snapped', params: { ...total, nudged: counts.nudged } });
  return { counts: total, newId };
}

const addBpm: TempoCommands['addBpm'] = (tx: Tx, { t, bpm, policy }) => {
  checkBpm(tx, bpm);
  checkPointBeat(tx, t);
  let id: BpmId;
  let counts = NO_RETIME;
  if (policy === 'keepBeats') {
    id = tx.newId('bpm');
    tx.insert('bpm', { id, t, bpm });
  } else {
    const r = keepTimeEdit(tx, { id: null, t, bpm }, null, t);
    id = r.newId as BpmId;
    counts = r.counts;
  }
  tx.setSelection({ bpm: [id] });
  tx.setFocus({ beat: t });
  return { id, ...counts };
};

const setBpm: TempoCommands['setBpm'] = (tx: Tx, { id, bpm, t, policy }) => {
  const p = bpmOf(tx, id);
  if (bpm !== undefined) checkBpm(tx, bpm);
  const first = isFirst(tx, id);
  if (t !== undefined && !Beats.eq(t, p.t)) {
    if (first) tx.fail({ key: 'cmd.tempo.firstPointFixed' });
    checkPointBeat(tx, t, id);
  }
  const nt = t ?? p.t;
  const nb = bpm ?? p.bpm;
  if (Beats.eq(nt, p.t) && nb === p.bpm) return NO_RETIME;
  tx.setFocus({ beat: nt });
  if (policy === 'keepBeats') {
    const patch: Patch<BpmPoint> = {};
    if (!Beats.eq(nt, p.t)) patch.t = nt;
    if (nb !== p.bpm) patch.bpm = nb;
    tx.update('bpm', id, patch);
    return NO_RETIME;
  }
  return keepTimeEdit(tx, { id, t: nt, bpm: nb }, null, first ? null : Beats.min(p.t, nt)).counts;
};

const deleteBpm: TempoCommands['deleteBpm'] = (tx: Tx, { id, policy }) => {
  const p = bpmOf(tx, id);
  if (isFirst(tx, id) || Beats.eq(p.t, Beats.ZERO)) tx.fail({ key: 'cmd.tempo.firstPointDelete' });
  tx.setSelection({ bpm: [] });
  if (policy === 'keepBeats') {
    tx.remove('bpm', id);
    return NO_RETIME;
  }
  return keepTimeEdit(tx, null, id, p.t).counts;
};

/** The policy names (for UIs that list them). */
export const TEMPO_POLICIES: readonly TempoEditPolicy[] = ['keepBeats', 'keepTime'];

export const tempoCommands: TempoCommands = {
  addBpm,
  setBpm,
  deleteBpm,
  ...timingCommands,
};
